import { FC, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { SpaceKind } from 'shared';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormInput } from '../../../../components/input/index';
import { ModalWrapper } from '../../../../components/modal-wrapper/index';
import { useLocale, useReactForm } from '../../../../hooks/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { useCreateFolder } from '../../hooks/use-create-folder.hook';
import { useSpaceNavigationFolders } from '../../hooks/use-space-navigation-folders.hook';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { TreeSpaceRef } from '../../types/data-room.types';
import { CreateFolderForm, createFolderSchema } from '../../validations/create-folder.validation';

type CreateFolderFieldsProps = {
  isOpen: boolean;
  onCancel: () => void;
  onCreated: () => void;
  parentFolderId: null | string;
  space: TreeSpaceRef;
};

// Mounted only while the modal is open, so the form starts empty on every opening. It owns the
// modal shell too: while a create is pending the modal cannot be dismissed, so a late response never
// closes a different modal opened in the meantime.
const CreateFolderFields: FC<CreateFolderFieldsProps> = ({
  isOpen,
  onCancel,
  onCreated,
  parentFolderId,
  space,
}) => {
  const { messagesLocale } = useLocale();
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
  } = useReactForm({ defaultValues: { name: '' }, schema: createFolderSchema });
  const name = useWatch({ control, name: 'name' });
  const { createFolder, isPending } = useCreateFolder({
    onDuplicateName: () =>
      setError('name', { message: messagesLocale['FOLDER_NAME_ALREADY_EXISTS'] }),
    onSuccess: onCreated,
  });
  // The open level is already cached, so a taken name is caught before the request is sent.
  const { folders: siblings } = useSpaceNavigationFolders({
    kind: space.kind,
    parentFolderId,
    spaceId: space.id,
  });
  const siblingNames = useMemo(
    () => new Set(siblings.map((folder) => folder.name.toLowerCase())),
    [siblings],
  );
  const isDuplicate = siblingNames.has(name.trim().toLowerCase());

  const canCreate = isValid && !isDuplicate && !isPending;

  const cancel = () => {
    if (!isPending) onCancel();
  };

  const submit = () => {
    void handleSubmit(({ name: folderName }: CreateFolderForm) =>
      createFolder({ name: folderName, parentFolderId, space }),
    )();
  };

  return (
    <ModalWrapper
      className="w-full max-w-[493px] shadow-none"
      control={{ onOpenChange: (open) => !open && cancel(), open: isOpen }}
      onBgClick={cancel}
      side="CENTER"
    >
      {/* A form, so that Enter in the field creates as the button does, and never while it cannot. */}
      <form
        className="flex w-full flex-col gap-8 rounded bg-white p-4 shadow-3xl"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();

          if (canCreate) submit();
        }}
      >
        <div className="flex flex-col gap-4">
          <span className="text-xl font-semibold text-gray-700">
            <AppFormattedMessage id="CREATE_NEW_FOLDER" />
          </span>
          <FormInput
            control={control}
            errorMessage={isDuplicate ? messagesLocale['FOLDER_NAME_ALREADY_EXISTS'] : undefined}
            name="name"
            placeholder={<AppFormattedMessage id="FOLDER" />}
          />
        </div>
        <div className="flex h-11 w-full justify-end gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-300 px-6 py-[10px] text-sm font-[450] text-gray-700"
            disabled={isPending}
            onClick={cancel}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-full w-fit px-6 py-[10px] text-sm font-[550]"
            disabled={!canCreate}
            isLoading={isPending}
            type="submit"
          >
            <AppFormattedMessage id="CREATE" />
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

// Creates the folder in whichever folder is open in the browser, like Open Drive.
export const CreateFolderModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const handleAccept = DataRoomModalStore(({ handleAccept }) => handleAccept);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);
  const selection = SidebarTreeStore((state) => state.selection);

  if (
    type !== DataRoomModalType.CREATE_FOLDER ||
    !selection ||
    selection.space.kind === SpaceKind.VAULT
  ) {
    return null;
  }

  return (
    <CreateFolderFields
      isOpen={isOpen}
      onCancel={handleDismiss}
      onCreated={handleAccept}
      parentFolderId={selection.folderId}
      space={{ id: selection.space.id, kind: selection.space.kind }}
    />
  );
};
